import handleBlockActionForShadowMailbox from '../../actions/helpers/handleBlockActionForShadowMailbox';
import copyItemsService from '../../services/copyItemsService';
import {
    confirmEmptyFolderAllSelectedConversationsText,
    confirmEmptyFolderAllSelectedItemsText,
} from '../../strings.locstring.json';
import { ObservableMap } from 'mobx';
import { ActionSource } from 'owa-analytics-actions';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { isGroupTableQuery } from 'owa-group-utils';
import loc, { format } from 'owa-localize';
import { searchFolderName } from 'owa-locstrings/lib/strings/searchfoldername.locstring.json';
import { lazyDiscardCompose } from 'owa-mail-compose-actions';
import { composeStore } from 'owa-mail-compose-store';
import { isDumpsterSearchTable } from 'owa-mail-list-search';
import getFolderScopeText from 'owa-mail-list-store/lib/selectors/getFolderScopeText';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import { getStore as getMailSearchStore, MailSearchStore } from 'owa-mail-search';
import { isDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';
import { isUnderDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';
import getServiceContextFolderId from 'owa-mail-store/lib/services/utils/getServiceContextFolderId';
import { NUMBER_OF_SELECTED_ITEMS_THRESHOLD } from 'owa-mail-store/lib/utils/bulkTriageConstants';
import { SearchScopeKind } from 'owa-search-service';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { select } from 'satcheljs/lib/legacy';
import {
    permanentlyDeleteSearchResultsConfirmationDialogSubText,
    deleteSearchResultsConfirmationDialogSubText,
} from './deleteMailListRowsHelper.locstring.json';
import {
    TableQueryType,
    TableView,
    getRowIdsFromRowKeys,
    isDumpsterTable,
    isConversationView,
} from 'owa-mail-list-store';
import folderStore, {
    isFolderOfTreeType,
    isPublicFolder,
    getEffectiveFolderDisplayName,
} from 'owa-folders';
import { MailFolder } from 'owa-graph-schema';
import {
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    SHARED_FOLDERS_TREE_TYPE,
} from 'owa-folders-constants';
import { getIsEverythingSelectedInTable } from 'owa-mail-list-store';

/**
 * Handler for deleting the rows
 */
type deleteRowsInternalHandler = (rowKeys: string[], disposalType: DisposalType) => void;

export interface DeleteMailListRowsState {
    originalTableView: TableView;
    folderTable: ObservableMap<string, MailFolder>;
    searchStore?: MailSearchStore;
}

export interface ConfirmDialogStrings {
    deleteNumberOfRowsTitle: string;
    deleteOneRowTitle: string;
    confirmDeleteRows: string;
}

export default select<DeleteMailListRowsState>({
    originalTableView: (conversationId: string, originalTableViewId: string) =>
        listViewStore.tableViews.get(originalTableViewId),
    folderTable: () => folderStore.folderTable,
    searchStore: () => getMailSearchStore(),
})(function deleteMailListRowsHelper(
    rowKeys: string[],
    originalTableViewId: string,
    isExplicitSoftDelete: boolean,
    deleteRowsInternal: deleteRowsInternalHandler,
    isReplyingToOneOfRows: (rowIds: string[]) => boolean,
    confirmDialogStrings: ConfirmDialogStrings,
    actionSource: ActionSource,
    state?: DeleteMailListRowsState
) {
    const originalTableView: TableView = state.originalTableView;
    const tableQuery = originalTableView.tableQuery;
    const sourceFolderId = tableQuery.folderId;
    let areRowsUnderDeletedItems;
    let sourceFolderName;

    // Early return if the action is blocked for shadow mailbox
    if (handleBlockActionForShadowMailbox(sourceFolderId, null)) {
        return null;
    }

    const isGroupMailbox = isGroupTableQuery(tableQuery);
    const isSearchTable = tableQuery.type == TableQueryType.Search;

    /**
     * Special case when "Delete all" is pressed from search results without
     * any selection. Need to check action source and ensure it's not from a
     * hover action (since there may not be a row selected in that scenario).
     */
    const isSearchDeleteAllWithoutSelection =
        isSearchTable && originalTableView.selectedRowKeys.size === 0 && actionSource !== 'Hover';

    const isPublicFolderId = isPublicFolder(sourceFolderId);
    const isDumpster = isDumpsterTable(tableQuery) || isDumpsterSearchTable(tableQuery);
    if (isGroupMailbox) {
        areRowsUnderDeletedItems = false;
    } else if (isSearchTable) {
        const staticSearchScope = state.searchStore.staticSearchScope;

        // OW:12312 [Refactor]handle batch conversation triage in search scenarios more elegantly
        // if we are searching in the Deleted Items folder, we want to permanently delete
        areRowsUnderDeletedItems =
            (staticSearchScope.kind == SearchScopeKind.PrimaryMailbox ||
                staticSearchScope.kind == SearchScopeKind.ArchiveMailbox) &&
            isDeletedItemsFolderFromAnyMailbox(staticSearchScope.folderId);
        sourceFolderName = loc(searchFolderName);
    } else if (!isDumpster && !isPublicFolderId) {
        const sourceFolder = state.folderTable.get(sourceFolderId);
        areRowsUnderDeletedItems =
            isDeletedItemsFolderFromAnyMailbox(sourceFolderId) ||
            isUnderDeletedItemsFolderFromAnyMailbox(sourceFolder);
        sourceFolderName = getEffectiveFolderDisplayName(sourceFolder);
    }

    /**
     * Designates scenarios in which the delete happening is an actual delete (i.e.
     * soft delete) and not just being moved to Deleted Items folder.
     */
    const isSoftDelete =
        isExplicitSoftDelete || areRowsUnderDeletedItems || isGroupMailbox || isPublicFolderId;
    const rowKeysLength = rowKeys.length;
    // We show confirmation dialog if selected rows count is more than the threshold.
    const selectionCountGreaterThanThreshold = rowKeysLength >= NUMBER_OF_SELECTED_ITEMS_THRESHOLD;

    // If all rows are selected, the user has the option to click empty folder. We want to prompt the user a dialog no matter the amount of items.
    const isEmptyFolderOperation = getIsEverythingSelectedInTable(originalTableView);

    const confirmationDialogTitle = getConfirmationDialogTitle(rowKeysLength, confirmDialogStrings);

    const confirmationDialogSubText = getConfirmationDialogSubText(
        confirmDialogStrings,
        isDumpster,
        isSearchDeleteAllWithoutSelection,
        isSoftDelete,
        originalTableView,
        sourceFolderName
    );

    const resolveImmediately = shouldConfirmationDialogResolveImmediately(
        isSoftDelete,
        selectionCountGreaterThanThreshold,
        isDumpster,
        isSearchDeleteAllWithoutSelection,
        isEmptyFolderOperation
    );

    return confirm(confirmationDialogTitle, confirmationDialogSubText, resolveImmediately, {
        // Wait until dialog is dismissed to avoid focus interruption
        delayCallbackAfterAnimation: true,
    }).then((response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            // Since row may have been deleted from the table while the confirm dialog was open,
            // we need to validate the row keys again before performing delete
            const filteredRowKeys = rowKeys.filter(rowKey => {
                const idx = originalTableView.rowKeys.indexOf(rowKey);
                return idx >= 0;
            });
            // check for the shared folder
            const isSharedFolder: boolean = isFolderOfTreeType(
                sourceFolderId,
                SHARED_FOLDERS_TREE_TYPE
            );
            if (isSharedFolder) {
                // For shared folder delete action type, destination folderid is calculated for DeletedItems folder
                const destinationFolderIdForSharedFolder = folderNameToId(
                    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID
                );
                const rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableView.id);

                /**
                 * For shared folder, delete item action is performed by first copying the item to deletedItems folder
                 * And then item is deleted from the source folder.
                 * Need to return this promise here, as in the case of deeplink window closes as soon as the promise is resolved
                 * hence we must wait for both copy and delete service calls to be resolved
                 */
                return copyItemsService(
                    rowIdStrings,
                    getServiceContextFolderId(destinationFolderIdForSharedFolder),
                    true /* RemoteExecute */
                ).then(() => {
                    if (filteredRowKeys.length > 0) {
                        deleteRowsIfNotReplying(
                            filteredRowKeys,
                            originalTableView,
                            'HardDelete' /* disposalType */,
                            deleteRowsInternal,
                            isReplyingToOneOfRows
                        );
                    }
                });
            }

            // If in dumpster, hard delete always. Otherwise, soft delete or move to deleted items.
            const disposalType = isDumpster
                ? 'HardDelete'
                : isSoftDelete
                ? 'SoftDelete'
                : 'MoveToDeletedItems';

            if (filteredRowKeys.length > 0) {
                return deleteRowsIfNotReplying(
                    filteredRowKeys,
                    originalTableView,
                    disposalType,
                    deleteRowsInternal,
                    isReplyingToOneOfRows
                );
            }
        }
        return Promise.resolve();
    });
});

/**
 * Proceeds to delete the given rows if user's not replying to one of the rows
 */
async function deleteRowsIfNotReplying(
    rowKeys: string[],
    originalTableView: TableView,
    disposalType: DisposalType,
    deleteRowsInternal: deleteRowsInternalHandler,
    isReplyingToOneOfRows: (rowIds: string[]) => boolean
) {
    const rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableView.id);

    // Check isReplyingToOneOfRows for the first time to see if there is active compose for the rows
    // so that we can call lazyDiscardCompose() to show discard dialog and wait for user's choice
    if (isReplyingToOneOfRows(rowIdStrings)) {
        await lazyDiscardCompose.importAndExecute(
            composeStore.viewStates.get(composeStore.primaryComposeId)
        );
    }

    // Check isReplyingToOneOfRows for the second time to check the result of user's choice.
    // If false, it means either
    //    1. There's no compose for these rows at all, or
    //    2. There was compose but user chose discard so compose was closed
    // Then we can continue delete the rows
    // If true, it means there is compose and user doesn't want to discard it. So we stop deleting now.
    if (!isReplyingToOneOfRows(rowIdStrings)) {
        // Since row may have been deleted from the table while the discard compose dialog was open,
        // we need to validate the row keys again before performing delete
        const filteredRowKeys = rowKeys.filter(
            rowKey => originalTableView.rowKeys.indexOf(rowKey) >= 0
        );

        if (filteredRowKeys.length > 0) {
            deleteRowsInternal(filteredRowKeys, disposalType);
        }
    }
}

/**
 * Gets the title of confirmation dialog that'll prompt user for delete
 * confirmation.
 */
const getConfirmationDialogTitle = (
    rowKeyCount: number,
    confirmDialogStrings: ConfirmDialogStrings
): string => {
    return rowKeyCount > 1
        ? format(confirmDialogStrings.deleteNumberOfRowsTitle, rowKeyCount)
        : confirmDialogStrings.deleteOneRowTitle;
};

/**
 * Gets the description string for the confirmation dialog that'll prompt user
 * for delete confirmation.
 */
const getConfirmationDialogSubText = (
    confirmDialogStrings: ConfirmDialogStrings,
    isDumpster: boolean,
    isSearchDeleteAllWithoutSelection: boolean,
    isSoftDelete: boolean,
    originalTableView: TableView,
    sourceFolderName: string
): string => {
    /**
     * If we're in search results (and "Delete all" without selected items
     * scenario), then use special strings that specify "search results" rather
     * than "conversations" or "items".
     */
    if (isSearchDeleteAllWithoutSelection) {
        return isSoftDelete
            ? loc(permanentlyDeleteSearchResultsConfirmationDialogSubText)
            : loc(deleteSearchResultsConfirmationDialogSubText);
    }

    /**
     * If we're not in search results, then use more generic strings that specify
     * "conversations" or "items".
     *
     * If we're in Dumpster, then use strings that inform the user that the items
     * will be permanently deleted.
     */
    const emptyFolderString = isConversationView(originalTableView)
        ? confirmEmptyFolderAllSelectedConversationsText
        : confirmEmptyFolderAllSelectedItemsText;
    return isSoftDelete || isDumpster
        ? // "Do you want to permanently delete the selected items?"
          confirmDialogStrings.confirmDeleteRows
        : // "Are you sure you want to move all the selected items/conversations in [sourceFolderName] to Deleted Items?"
          format(
              loc(emptyFolderString),
              getFolderScopeText(
                  originalTableView.tableQuery.type,
                  originalTableView,
                  sourceFolderName
              )
          );
};

/**
 * Determines if confirmation dialog that prompts user for delete confirmation
 * should be resolved immediately (i.e. doesn't get shown).
 */
const shouldConfirmationDialogResolveImmediately = (
    isSoftDelete: boolean,
    selectionCountGreaterThanThreshold: boolean,
    isDumpster: boolean,
    isSearchDeleteAllWithoutSelection: boolean,
    isEmptyFolderOperation: boolean
): boolean => {
    /**
     * If any of the following conditions are true, then the confirmation dialog
     * shouldn't be resolved immediately (i.e. it should be shown to the user
     * for confirmation).
     *
     * - Is it an empty folder operation?
     * - Is item being permanently deleted?
     * - Does user have more items selected (in select all) than safety threshold?
     * - Is the user deleting items within Dumpster?
     * - Did user click "Delete All" in search results?
     */
    return !(
        isEmptyFolderOperation ||
        isSoftDelete ||
        selectionCountGreaterThanThreshold ||
        isDumpster ||
        isSearchDeleteAllWithoutSelection
    );
};
